import type { Step } from 'shepherd.js'
import type { TourDefinition } from '../registry'
import { back, finish, gotIt, next } from '../utils/buttons'
import { popoverOffset } from '../utils/popover-offset'

/**
 * Walks the user through creating their first market-research report.
 *
 * Steps 1-2 anchor on the page (welcome + Create Report button). Clicking
 * the button opens the create dialog AND advances the tour via Shepherd's
 * `advanceOn`. Steps 3-5 anchor on the form fields that appear after the
 * dialog mounts. Step 6 spotlights the submit button — clicking it (or
 * Finish) completes the tour.
 *
 * `showOn: () => true` on the field steps bypasses the default existence
 * check — without it, Shepherd would skip the field steps because the
 * default `showOn` runs synchronously when `tour.next()` is called and the
 * Radix dialog hasn't mounted yet. The default `beforeShowPromise` then
 * waits up to 1.5 s for each anchor (plenty of time for a Radix mount).
 *
 * Cancel cleanup: the first dialog-scoped step (`field-name`) registers a
 * one-shot `tour.once('cancel')` listener that dispatches `tour-mrr-close-
 * dialog`. The dialog component listens for it and closes — so dismissing
 * the tour mid-flow doesn't strand the user staring at an unexplained form.
 */
export const marketResearchIntroTour: TourDefinition = {
  id: 'market-research-intro',
  displayName: 'Market research intro',
  featureFlag: 'tour-market-research-intro',
  trigger: 'auto',
  targetPath: '/market-research',
  steps: [
    {
      id: 'welcome',
      title: 'Research any market',
      text: 'AI-generated reports on industries, market segments, and buyer profiles — tailored to your business.',
      buttons: [gotIt],
    },
    {
      id: 'create-btn',
      title: 'Start a new report',
      text: 'Click "Create Report" to open the new-report panel — we\'ll walk you through filling it out.',
      // bottom-end aligns the popover's right edge to the button's right edge,
      // so it hangs down and to the LEFT of the (top-right-of-page) button.
      attachTo: { element: '[data-tour="market-research-create-btn"]', on: 'bottom-end' },
      floatingUIOptions: popoverOffset(0, 12),
      // Auto-advance when the user clicks the trigger. Radix opens the dialog
      // from the same click; step 3 waits for the first field to mount.
      advanceOn: { selector: '[data-tour="market-research-create-btn"]', event: 'click' },
      buttons: [back],
    },
    {
      id: 'field-name',
      title: 'Name your report',
      text: 'Give it a short, memorable title — something like "AI in Manufacturing" or "EV drivetrain". This is how it\'ll appear in your reports library.',
      attachTo: { element: '[data-tour="market-research-field-name"]', on: 'right' },
      floatingUIOptions: popoverOffset(20, 0),
      // Default showOn would synchronously skip this step because the dialog
      // hasn't mounted yet. Default beforeShowPromise still waits ≤1.5 s for
      // the anchor.
      showOn: () => true,
      // First dialog-scoped step owns the cancel cleanup: if the user
      // dismisses the tour while inside the dialog, the dialog should
      // close. `once` auto-removes after firing.
      when: {
        show(this: Step) {
          this.tour.once('cancel', () => {
            globalThis.dispatchEvent(new CustomEvent('tour-mrr-close-dialog'))
          })
        },
      },
      // No Back — the previous step's anchor (Create Report button) is now
      // hidden behind the open dialog.
      buttons: [next],
    },
    {
      id: 'field-industry',
      title: 'Pick an industry (optional)',
      text: 'Narrow the report to one industry like Manufacturing, Healthcare, or Logistics — leave it blank for cross-industry analysis.',
      attachTo: { element: '[data-tour="market-research-field-industry"]', on: 'right' },
      floatingUIOptions: popoverOffset(20, 0),
      showOn: () => true,
      buttons: [back, next],
    },
    {
      id: 'field-description',
      title: 'Describe the angle (optional)',
      text: 'Give us specifics: "Companies adopting AI for predictive maintenance in factories", or "Distributors selling automation equipment in the Midwest". The more concrete you are, the sharper the report.',
      attachTo: { element: '[data-tour="market-research-field-description"]', on: 'right' },
      floatingUIOptions: popoverOffset(20, 0),
      showOn: () => true,
      buttons: [back, next],
    },
    {
      id: 'submit-btn',
      title: 'Generate your report',
      text: 'When you\'re ready, click "Create Report" to kick off generation. Reports take about a minute — we\'ll notify you when it\'s ready.',
      attachTo: { element: '[data-tour="market-research-submit-btn"]', on: 'top' },
      floatingUIOptions: popoverOffset(0, -12),
      showOn: () => true,
      // Clicking the actual submit button completes the tour too — Shepherd
      // advances past the last step, which fires `complete`. The mutation
      // fires in parallel from the button's own onClick.
      advanceOn: { selector: '[data-tour="market-research-submit-btn"]', event: 'click' },
      buttons: [back, finish],
    },
  ],
}
